/**
 * Ablaufdiagramm: die Schrittfolge des Bodenmanagements als Kette mit
 * Entscheidungsknoten.
 *
 * Reine Funktion, kein DOM, kein jsPDF. Vorbild: das Fliessdiagramm
 * Bodenmanagement (Anlage 1 des Musterplans).
 *
 * ═══ WOFUER DIESE ZEICHNUNG DA IST ════════════════════════════════════════
 * Der Umgang mit belastetem Aushub ist eine REIHENFOLGE, und fast jeder
 * Fehler darin ist ein Reihenfolgefehler: die Miete wird abgefahren, bevor
 * sie beprobt ist; die Freigabe kommt nach dem Abtransport; der
 * Entsorgungsweg steht fest, bevor die Analytik vorliegt. In Fliesstext
 * gegossen liest sich das wie eine Aufzaehlung, aus der man sich bedienen
 * kann. Als Kette gezeichnet ist die Reihenfolge die Aussage.
 *
 * ═══ WARUM DER ENTSCHEIDUNGSKNOTEN AUS LINIEN BESTEHT ═════════════════════
 * Eine Raute waere eine neue Primitiv-Art — und die muesste in BEIDEN
 * Renderern nachgezogen werden (SVG und jsPDF). Vier Linien tun dasselbe,
 * laufen durch beide Renderer unveraendert und koennen dort nicht
 * auseinanderlaufen. Neue Primitive nur, wenn sich etwas nicht zusammensetzen
 * laesst.
 *
 * ═══ WAS DER TEST NAMENTLICH PRUEFT ═══════════════════════════════════════
 * Kein Pfeil zeigt zurueck nach oben, und die beiden Entsorgungswege liegen
 * UNTER dem Beprobungsschritt. Ein Diagramm, in dem der Entsorgungsweg vor
 * der Beprobung steht, ist eine gedruckte Anleitung zum Entsorgen nach
 * Vermutung.
 */

import type { Primitive, Zeichnung } from './primitive';
import { kasten, umbruch } from './primitive';

export interface AblaufSchritt {
    /** Nummer wie im Musterplan, z. B. '(1)'. */
    nr: string;
    text: string;
    art: 'schritt' | 'entscheidung';
    /** Nur bei 'entscheidung': Beschriftung des Ja-Ausgangs. */
    ja?: string;
    /** Nur bei 'entscheidung': Beschriftung des Nein-Ausgangs. */
    nein?: string;
}

export interface AblaufParameter {
    titel?: string;
    schritte: AblaufSchritt[];
}

/**
 * Der Regelablauf des Bodenmanagements.
 *
 * Die Kette ist bewusst vollstaendig bis zur Nachweisfuehrung: der haeufigste
 * Mangel ist nicht ein falscher Schritt, sondern ein weggelassener.
 */
export const ABLAUF_STANDARD: AblaufParameter = {
    titel: 'Bodenmanagement: Ablauf',
    schritte: [
        { nr: '(1)', art: 'schritt', text: 'Aushub getrennt nach Rasterfeld und Schicht' },
        {
            nr: '(2)', art: 'schritt',
            text: 'Getrennte Zwischenlagerung in Mieten auf befestigter, abgedichteter Fläche im Schwarzbereich',
        },
        {
            nr: '(3)', art: 'schritt',
            text: 'Beprobung der Miete, Deklarationsanalytik nach LAGA / EBV',
        },
        {
            nr: '(4)', art: 'entscheidung',
            text: 'Zuordnungswert eingehalten?',
            ja: 'Verwertung nach EBV: Einbau mit Einbautabelle',
            nein: 'Entsorgung: Behandlung oder Deponie nach AVV-Schlüssel',
        },
        {
            nr: '(5)', art: 'schritt',
            text: 'Abfallrechtliche Deklaration, Entsorgernachweis, Beförderer benennen',
        },
        {
            nr: '(6)', art: 'schritt',
            text: 'Schriftliche Freigabe der Miete durch Gutachter und Koordinator',
        },
        {
            nr: '(7)', art: 'schritt',
            text: 'Abtransport mit Begleitschein, Reifenwaschung an der Ausfahrt',
        },
        {
            nr: '(8)', art: 'schritt',
            text: 'Nachweisführung: Wiegescheine, Übernahmescheine, Entsorgungsnachweise zum Plan',
        },
        {
            nr: '(9)', art: 'schritt',
            text: 'Rückverfüllung mit unbelastetem Material, Trennlage und Abdeckung; Freigabe des Weißbereichs',
        },
    ],
};

// ─── Geometrie in Millimetern ───────────────────────────────────────────────

const RAND = 14;
const GESAMT_B = 134;
const BOX_B = 104;
const ZWEIG_B = 62;
/** Waagerechter Versatz der beiden Zweige von der Mittelachse. */
const ZWEIG_DX = 35;
const RAUTE_B = 96;
const RAUTE_H = 24;
const ABSTAND = 9;

interface Punkt { x: number; y: number }

const kastenHoehe = (zeilen: number): number => 8 + zeilen * 3.8;

export const zeichneAblauf = (p: AblaufParameter): Zeichnung => {
    const teile: Primitive[] = [];
    const cx = RAND + GESAMT_B / 2;

    let y = RAND;
    /** Offene Enden, aus denen der Pfeil zum naechsten Schritt kommt. */
    let anschluss: Punkt[] = [];

    /** Fuehrt die offenen Enden auf den naechsten Kasten zusammen. */
    const verbinde = (zielY: number) => {
        for (const a of anschluss) {
            teile.push({
                art: 'pfeil', x1: a.x, y1: a.y, x2: cx, y2: zielY,
                linie: 'linie_stark', staerke: 0.5, spitze: 3,
            });
        }
        anschluss = [];
    };

    for (const s of p.schritte) {
        if (s.art === 'entscheidung') {
            verbinde(y);

            // ── Entscheidungsknoten als Raute aus vier Linien ──────────────
            const oben = { x: cx, y };
            const rechts = { x: cx + RAUTE_B / 2, y: y + RAUTE_H / 2 };
            const unten = { x: cx, y: y + RAUTE_H };
            const links = { x: cx - RAUTE_B / 2, y: y + RAUTE_H / 2 };
            const kanten: [Punkt, Punkt][] = [
                [oben, rechts], [rechts, unten], [unten, links], [links, oben],
            ];
            for (const [a, bb] of kanten) {
                teile.push({
                    art: 'linie', x1: a.x, y1: a.y, x2: bb.x, y2: bb.y,
                    linie: 'warnung', staerke: 0.5,
                });
            }
            const frage = umbruch(`${s.nr} ${s.text}`, 30).slice(0, 2);
            frage.forEach((t, i) => {
                teile.push({
                    art: 'text', x: cx,
                    y: y + RAUTE_H / 2 - (frage.length - 1) * 1.9 + i * 3.8 + 1,
                    text: t, groesse: 6, fett: true, anker: 'mitte', farbe: 'text',
                });
            });

            // ── die beiden Ausgaenge ──────────────────────────────────────
            const yZweig = y + RAUTE_H + ABSTAND + 3;
            const zweige: { dx: number; marke: string; text: string; benannt: boolean }[] = [
                { dx: -ZWEIG_DX, marke: 'ja', text: (s.ja ?? '').trim(), benannt: !!(s.ja ?? '').trim() },
                { dx: ZWEIG_DX, marke: 'nein', text: (s.nein ?? '').trim(), benannt: !!(s.nein ?? '').trim() },
            ];

            let untersteZweigKante = yZweig;
            for (const z of zweige) {
                teile.push({
                    art: 'pfeil',
                    x1: cx, y1: y + RAUTE_H,
                    x2: cx + z.dx, y2: yZweig,
                    linie: 'linie_stark', staerke: 0.5, spitze: 3,
                });
                teile.push({
                    art: 'text',
                    x: cx + z.dx / 2 + (z.dx < 0 ? -2 : 2),
                    y: y + RAUTE_H + ABSTAND / 2,
                    text: z.marke, groesse: 6, fett: true,
                    anker: z.dx < 0 ? 'rechts' : 'links', farbe: 'warnung',
                });

                const zeilen = z.benannt
                    ? umbruch(z.text, 22).slice(0, 3)
                    : ['OFFEN: Ausgang', 'nicht benannt'];
                const h = kastenHoehe(zeilen.length);
                teile.push(...kasten(
                    cx + z.dx - ZWEIG_B / 2, yZweig, ZWEIG_B, h,
                    z.benannt ? 'flaeche_hell' : 'weissbereich',
                    zeilen.map(t => ({
                        text: t, groesse: 6, farbe: z.benannt ? 'text' : 'warnung',
                    })),
                    z.benannt ? 'linie_stark' : 'warnung',
                ));
                anschluss.push({ x: cx + z.dx, y: yZweig + h });
                untersteZweigKante = Math.max(untersteZweigKante, yZweig + h);
            }

            y = untersteZweigKante + ABSTAND;
            continue;
        }

        // ── gewoehnlicher Schritt ─────────────────────────────────────────
        const zeilen = umbruch(`${s.nr} ${s.text}`, 42).slice(0, 3);
        const h = kastenHoehe(zeilen.length);
        verbinde(y);
        teile.push(...kasten(
            cx - BOX_B / 2, y, BOX_B, h, 'flaeche_hell',
            zeilen.map((t, i) => ({ text: t, groesse: 6.5, fett: i === 0 })),
        ));
        anschluss = [{ x: cx, y: y + h }];
        y += h + ABSTAND;
    }

    return {
        breite: GESAMT_B + 2 * RAND,
        hoehe: y - ABSTAND + RAND,
        titel: p.titel || 'Ablauf',
        untertitel: 'Schrittfolge mit Entscheidung über den Entsorgungsweg',
        teile,
        fussnoten: [
            'Der Entsorgungsweg steht erst nach der Beprobung fest. Wer die Miete vorher '
            + 'zuordnet, entsorgt nach Vermutung.',
            'Jeder Schritt wird schriftlich nachgewiesen; die Nachweise sind Anlage zu diesem '
            + 'Plan.',
        ],
    };
};
